// skipcq: JS-W1028, JS-0128
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Rating,
  Stack,
  Grid,
} from '@mui/material';

import BeerProfileRatingPanel from '../pintPortfolio/BeerProfileRatingPanel';
import ReviewReactionDock from './ReviewReactionDock';
import ReviewPhotosStrip from './ReviewPhotosStrip';
import ReviewDetailsNewChip from '../reviewDetails/ReviewDetailsNewChip';
import { getReviewTagLabelsForCategory } from '../../utils/brewhouseBeerStats';
import { formatReviewDateLine } from '../../utils/formatReviewDateLine';

const PROFILE_RATING_DIMENSIONS = [
  { key: 'overall', label: 'Overall' },
  { key: 'appearance', label: 'Appearance', tagCategory: 'appearance' },
  { key: 'aroma', label: 'Aroma', tagCategory: 'aroma' },
  { key: 'flavor', label: 'Flavor', tagCategory: 'flavor' },
  { key: 'mouthfeel', label: 'Mouthfeel', tagCategory: 'mouthfeel' },
];

const CONSUMPTION_PLACE_LABEL = 'Where did the pint go down?';

function getConsumptionPlaceDisplayLabel(review) {
  const consumptionType = review?.consumptionPlaceType;
  if (!consumptionType) {
    return null;
  }
  if (consumptionType === 'home') {
    return 'Home';
  }
  if (consumptionType === 'brewery') {
    return review.consumptionPlaceName || 'Brewery';
  }
  if (consumptionType === 'venue') {
    return review.consumptionPlaceName || 'Venue';
  }
  if (consumptionType === 'event') {
    return review.consumptionPlaceName || 'Event';
  }
  return null;
}

function ConsumptionPlaceSection({ review }) {
  const displayLabel = getConsumptionPlaceDisplayLabel(review);
  if (!displayLabel) {
    return null;
  }

  return (
    <Box sx={{ pt: 1.5 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {CONSUMPTION_PLACE_LABEL}
      </Typography>
      <Typography variant="body2">{displayLabel}</Typography>
    </Box>
  );
}

ConsumptionPlaceSection.propTypes = {
  review: PropTypes.shape({
    consumptionPlaceType: PropTypes.string,
    consumptionPlaceName: PropTypes.string,
  }).isRequired,
};

function ProfileReviewRatings({ ratings, tags, tagLabelMap, review }) {
  const panels = PROFILE_RATING_DIMENSIONS.map((dimension) => {
    const tagEntries = dimension.tagCategory
      ? getReviewTagLabelsForCategory(tags, tagLabelMap, dimension.tagCategory)
      : [];
    const hasRating = ratings?.[dimension.key] !== undefined;
    const hasTags = tagEntries.length > 0;

    if (!hasRating && !hasTags) {
      return null;
    }

    return (
      <BeerProfileRatingPanel
        key={dimension.key}
        label={dimension.label}
        rating={ratings?.[dimension.key]}
        tags={tagEntries}
        unratedLabel="No rating"
        hideIconsWhenUnrated
        showTagCounts={false}
      />
    );
  }).filter(Boolean);

  const hasConsumptionPlace = Boolean(getConsumptionPlaceDisplayLabel(review));

  if (panels.length === 0 && !hasConsumptionPlace) {
    return null;
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: { xs: 1.5, sm: 2 },
        bgcolor: 'background.paper',
      }}
    >
      {panels}
      <ConsumptionPlaceSection review={review} />
    </Box>
  );
}

ProfileReviewRatings.propTypes = {
  ratings: PropTypes.object,
  tags: PropTypes.object,
  tagLabelMap: PropTypes.objectOf(PropTypes.string).isRequired,
  review: PropTypes.shape({
    consumptionPlaceType: PropTypes.string,
    consumptionPlaceName: PropTypes.string,
  }).isRequired,
};

function FullReviewRatings({ ratings, review }) {
  if (!ratings || Object.keys(ratings).length === 0) {
    return null;
  }

  return (
    <Box>
      <Stack spacing={1.5}>
        {ratings.appearance !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 100 }}>
              Appearance:
            </Typography>
            <Rating value={ratings.appearance} max={5} readOnly size="small" />
          </Box>
        )}
        {ratings.aroma !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 100 }}>
              Aroma:
            </Typography>
            <Rating value={ratings.aroma} max={5} readOnly size="small" />
          </Box>
        )}
        {ratings.flavor !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 100 }}>
              Flavor:
            </Typography>
            <Rating value={ratings.flavor} max={5} readOnly size="small" />
          </Box>
        )}
        {ratings.mouthfeel !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 100 }}>
              Mouthfeel:
            </Typography>
            <Rating value={ratings.mouthfeel} max={5} readOnly size="small" />
          </Box>
        )}
        {ratings.overall !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 100 }}>
              Overall:
            </Typography>
            <Rating value={ratings.overall} max={5} readOnly size="small" />
          </Box>
        )}
        <ConsumptionPlaceSection review={review} />
      </Stack>
    </Box>
  );
}

FullReviewRatings.propTypes = {
  ratings: PropTypes.object,
  review: PropTypes.shape({
    consumptionPlaceType: PropTypes.string,
    consumptionPlaceName: PropTypes.string,
  }).isRequired,
};

function BeerReviewUserMeta({ userName, date, editedAt, experienceDate, comment }) {
  return (
    <>
      <Typography variant="body2" gutterBottom sx={{ color: 'text.secondary', mb: 0 }}>
        User: {userName}
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ color: 'text.secondary' }}>
        Date: {formatReviewDateLine(date, editedAt, experienceDate)}
      </Typography>
      {comment ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            mt: 2,
            whiteSpace: 'pre-wrap',
          }}
        >
          {comment}
        </Typography>
      ) : null}
    </>
  );
}

BeerReviewUserMeta.propTypes = {
  userName: PropTypes.string.isRequired,
  date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]).isRequired,
  editedAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  experienceDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  comment: PropTypes.string,
};

function ReviewDetailsProfileLayout({
  review,
  tagLabelMap,
  breweryId,
  beerId,
  showBreweryReactionDock,
  showNewChip,
  analyticsSource,
}) {
  const { userName, date, comment, ratings, tags } = review;

  return (
    <Box sx={{ position: 'relative' }}>
      {showNewChip ? (
        <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
          <ReviewDetailsNewChip />
        </Box>
      ) : null}
      <Stack spacing={2} sx={{ pt: showNewChip ? 3 : 0 }}>
        <BeerReviewUserMeta
          userName={userName}
          date={date}
          editedAt={review.editedAt}
          experienceDate={review.experienceDate}
          comment={comment}
        />
        <ReviewPhotosStrip
          reviewType="beer"
          reviewId={review.id}
          photoIds={review.photoIds}
          photoSummary={review.photoSummary}
        />
        <ProfileReviewRatings
          ratings={ratings}
          tags={tags}
          tagLabelMap={tagLabelMap}
          review={review}
        />
        {showBreweryReactionDock && breweryId
          && (analyticsSource !== 'pint-portfolio' || beerId) ? (
          <ReviewReactionDock
            breweryId={breweryId}
            beerId={beerId}
            review={review}
            reviewType="beer"
            enabled={review.breweryId === breweryId}
            analyticsSource={analyticsSource}
          />
        ) : null}
      </Stack>
    </Box>
  );
}

ReviewDetailsProfileLayout.propTypes = {
  review: PropTypes.shape({
    id: PropTypes.string,
    userName: PropTypes.string.isRequired,
    date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]).isRequired,
    editedAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    experienceDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    comment: PropTypes.string,
    ratings: PropTypes.object,
    tags: PropTypes.object,
    breweryId: PropTypes.string,
    consumptionPlaceType: PropTypes.string,
    consumptionPlaceName: PropTypes.string,
    photoIds: PropTypes.arrayOf(PropTypes.string),
    photoSummary: PropTypes.object,
  }).isRequired,
  tagLabelMap: PropTypes.objectOf(PropTypes.string).isRequired,
  breweryId: PropTypes.string,
  beerId: PropTypes.string,
  showBreweryReactionDock: PropTypes.bool,
  showNewChip: PropTypes.bool,
  analyticsSource: PropTypes.oneOf(['pint-portfolio', 'review-details']),
};

/**
 * BeerReviewCard component to display a single beer review
 * @param {object} props
 * @param {object} props.review
 * @param {'full' | 'profile'} [props.variant]
 * @param {Record<string, string>} [props.tagLabelMap]
 */
function BeerReviewCard({
  review,
  variant = 'full',
  tagLabelMap = {},
  breweryId,
  beerId,
  showBreweryReactionDock = false,
  showNewChip = false,
  analyticsSource = 'pint-portfolio',
}) {
  const { beerName, breweryName, userName, date, comment, ratings, tags } = review;
  const showBeerMetadata = variant === 'full';
  const isProfileVariant = variant === 'profile';
  const isReviewDetailsLayout = isProfileVariant && analyticsSource === 'review-details';

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: 600,
        mb: 2,
        mx: isProfileVariant ? 'auto' : undefined,
      }}
    >
      <CardContent>
        {isReviewDetailsLayout ? (
          <ReviewDetailsProfileLayout
            review={review}
            tagLabelMap={tagLabelMap}
            breweryId={breweryId}
            beerId={beerId}
            showBreweryReactionDock={showBreweryReactionDock}
            showNewChip={showNewChip}
            analyticsSource={analyticsSource}
          />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {showBeerMetadata && (
                <>
                  <Typography variant="h6" component="div" gutterBottom>
                    {beerName}
                  </Typography>
                  <Typography variant="body2" gutterBottom sx={{ color: 'text.secondary' }}>
                    Brewery: {breweryName}
                  </Typography>
                </>
              )}
              <BeerReviewUserMeta
                userName={userName}
                date={date}
                editedAt={review.editedAt}
                experienceDate={review.experienceDate}
                comment={comment}
              />
              <ReviewPhotosStrip
                reviewType="beer"
                reviewId={review.id}
                photoIds={review.photoIds}
                photoSummary={review.photoSummary}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {isProfileVariant ? (
                <ProfileReviewRatings
                  ratings={ratings}
                  tags={tags}
                  tagLabelMap={tagLabelMap}
                  review={review}
                />
              ) : (
                <FullReviewRatings ratings={ratings} review={review} />
              )}
              {isProfileVariant && showBreweryReactionDock && breweryId
                && (analyticsSource !== 'pint-portfolio' || beerId) && (
                <ReviewReactionDock
                  breweryId={breweryId}
                  beerId={beerId}
                  review={review}
                  reviewType="beer"
                  enabled={review.breweryId === breweryId}
                  analyticsSource={analyticsSource}
                />
              )}
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
}

BeerReviewCard.propTypes = {
  review: PropTypes.shape({
    id: PropTypes.string,
    breweryId: PropTypes.string,
    beerName: PropTypes.string.isRequired,
    breweryName: PropTypes.string.isRequired,
    userName: PropTypes.string.isRequired,
    date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
      .isRequired,
    editedAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    experienceDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
    comment: PropTypes.string,
    ratings: PropTypes.shape({
      appearance: PropTypes.number,
      aroma: PropTypes.number,
      flavor: PropTypes.number,
      mouthfeel: PropTypes.number,
      overall: PropTypes.number,
    }),
    tags: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    consumptionPlaceType: PropTypes.string,
    consumptionPlaceId: PropTypes.string,
    consumptionPlaceName: PropTypes.string,
    reactionSummary: PropTypes.object,
    photoIds: PropTypes.arrayOf(PropTypes.string),
    photoRevision: PropTypes.number,
    photoSummary: PropTypes.object,
  }).isRequired,
  variant: PropTypes.oneOf(['full', 'profile']),
  tagLabelMap: PropTypes.objectOf(PropTypes.string),
  breweryId: PropTypes.string,
  beerId: PropTypes.string,
  showBreweryReactionDock: PropTypes.bool,
  showNewChip: PropTypes.bool,
  analyticsSource: PropTypes.oneOf(['pint-portfolio', 'review-details']),
};

export default BeerReviewCard;
